import React, { FC, PointerEvent, useEffect, useRef, useState } from "react";

import {
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "../../clientPortal/PortalModal";
import { initialsOf } from "../signature.utils";

export const SIGNATURE_FONTS = ["Caveat", "Dancing Script", "Homemade Apple"];

const FONT_LINK_ID = "signature-fonts";
const loadFonts = () => {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=Dancing+Script:wght@600&family=Homemade+Apple&display=swap";
  document.head.appendChild(link);
};

/** Draws typed text in a cursive font as a transparent PNG (what the PDF gets). */
export const typedImage = async (text: string, font: string) => {
  try {
    await document.fonts.load(`64px "${font}"`, text);
  } catch {
    /* falls back to the browser's cursive font */
  }
  const size = 64;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  ctx.font = `${size}px "${font}", cursive`;
  const width = Math.ceil(ctx.measureText(text).width) + 40;
  canvas.width = Math.max(width, 80);
  canvas.height = Math.round(size * 1.7);
  ctx.font = `${size}px "${font}", cursive`;
  ctx.fillStyle = "#1F2433";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 20, canvas.height / 2);
  return canvas.toDataURL("image/png");
};

/** The drawn strokes cut to their own size, so they fill the field. */
const trimmedImage = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext("2d")!;
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++)
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
  if (maxX < 0) return null;
  const pad = 8;
  const out = document.createElement("canvas");
  out.width = maxX - minX + 1 + pad * 2;
  out.height = maxY - minY + 1 + pad * 2;
  out
    .getContext("2d")!
    .drawImage(
      canvas,
      minX,
      minY,
      out.width - pad * 2,
      out.height - pad * 2,
      pad,
      pad,
      out.width - pad * 2,
      out.height - pad * 2,
    );
  return out.toDataURL("image/png");
};

const DrawPad: FC<{
  onChange: (empty: boolean) => void;
  padRef: (el: HTMLCanvasElement | null) => void;
}> = ({ onChange, padRef }) => {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = el.clientWidth * ratio;
    el.height = el.clientHeight * ratio;
    const ctx = el.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1F2433";
  }, []);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    const p = point(e);
    const ctx = e.currentTarget.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    onChange(false);
  };
  const up = () => {
    drawing.current = false;
    last.current = null;
  };

  return (
    <canvas
      ref={(el) => {
        canvas.current = el;
        padRef(el);
      }}
      aria-label={"Draw your signature"}
      className={
        "h-[150px] w-full touch-none rounded-[10px] border-[1.5px] border-dashed border-[#C9CFDB] bg-[#FBFBFD]"
      }
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={up}
    />
  );
};

export type AdoptedSignature = {
  signatureImage: string;
  initialsImage: string;
  signedName: string;
};

/** "Adopt your signature": type it (cursive) or draw it, then agree to sign electronically. */
export const AdoptSignatureModal: FC<{
  defaultName: string;
  firmName: string;
  needsInitials: boolean;
  onClose: () => void;
  onAdopt: (adopted: AdoptedSignature) => void;
}> = ({ defaultName, firmName, needsInitials, onClose, onAdopt }) => {
  const [mode, setMode] = useState<"type" | "draw">("type");
  const [name, setName] = useState(defaultName);
  const [initials, setInitials] = useState(initialsOf(defaultName));
  const [font, setFont] = useState(SIGNATURE_FONTS[0]);
  const [agree, setAgree] = useState(false);
  const [drawnEmpty, setDrawnEmpty] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pad = useRef<HTMLCanvasElement | null>(null);

  useEffect(loadFonts, []);

  const clearPad = () => {
    const el = pad.current;
    if (!el) return;
    el.getContext("2d")!.clearRect(0, 0, el.width, el.height);
    setDrawnEmpty(true);
  };

  const adopt = async () => {
    setError(null);
    const cleanName = name.trim();
    if (!cleanName) return setError("Type your full name.");
    if (needsInitials && !initials.trim())
      return setError("Type your initials.");
    if (mode === "draw" && drawnEmpty)
      return setError("Draw your signature in the box.");
    if (!agree)
      return setError("Please tick the box to agree to sign electronically.");
    setBusy(true);
    try {
      const signatureImage =
        mode === "draw" && pad.current
          ? trimmedImage(pad.current)
          : await typedImage(cleanName, font);
      if (!signatureImage) return setError("Draw your signature in the box.");
      const initialsImage = await typedImage(
        initials.trim() || initialsOf(cleanName),
        font,
      );
      onAdopt({ signatureImage, initialsImage, signedName: cleanName });
    } finally {
      setBusy(false);
    }
  };

  const tab = (value: "type" | "draw", label: string) => (
    <button
      type={"button"}
      role={"tab"}
      aria-selected={mode === value}
      onClick={() => setMode(value)}
      className={`h-9 flex-1 rounded-[8px] text-[13px] font-bold ${
        mode === value
          ? "bg-white text-[#1F2433] shadow-[0_1px_3px_rgba(31,36,51,0.12)]"
          : "text-[#6B7185]"
      }`}>
      {label}
    </button>
  );

  return (
    <PortalModal
      title={"Adopt your signature"}
      subtitle={"It will be placed everywhere you're asked to sign."}
      onClose={onClose}
      width={600}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            onClick={adopt}
            disabled={busy}>
            {busy ? "One moment…" : "Adopt and sign"}
          </button>
        </>
      }>
      <div className={"grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]"}>
        <label
          className={
            "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]"
          }>
          Full name
          <input
            className={modalInput}
            value={name}
            maxLength={120}
            onChange={(e) => {
              setName(e.target.value);
              setInitials(initialsOf(e.target.value));
            }}
          />
        </label>
        {needsInitials && (
          <label
            className={
              "flex flex-col gap-1.5 text-[13px] font-bold text-[#3A4256]"
            }>
            Initials
            <input
              className={modalInput}
              value={initials}
              maxLength={4}
              onChange={(e) => setInitials(e.target.value.toUpperCase())}
            />
          </label>
        )}
      </div>

      <div
        role={"tablist"}
        className={"flex gap-1 rounded-[10px] bg-[#F1F2F5] p-1"}>
        {tab("type", "Type")}
        {tab("draw", "Draw")}
      </div>

      {mode === "type" ? (
        <div className={"flex flex-col gap-2"}>
          {SIGNATURE_FONTS.map((f) => (
            <label
              key={f}
              className={`flex cursor-pointer items-center gap-3 rounded-[10px] border-[1.5px] px-4 py-2 ${
                font === f
                  ? "border-[#6B4EFF] bg-[#F7F5FF]"
                  : "border-[#E4E7EE]"
              }`}>
              <input
                type={"radio"}
                name={"sigfont"}
                checked={font === f}
                onChange={() => setFont(f)}
                className={"accent-[#6B4EFF]"}
              />
              <span
                className={
                  "flex-1 truncate text-[30px] leading-[48px] text-[#1F2433]"
                }
                style={{ fontFamily: `"${f}", cursive` }}>
                {name.trim() || "Your name"}
              </span>
              {needsInitials && (
                <span
                  className={"text-[26px] text-[#1F2433]"}
                  style={{ fontFamily: `"${f}", cursive` }}>
                  {initials || "AB"}
                </span>
              )}
            </label>
          ))}
        </div>
      ) : (
        <div className={"flex flex-col gap-1.5"}>
          <DrawPad
            onChange={setDrawnEmpty}
            padRef={(el) => (pad.current = el)}
          />
          <div
            className={
              "flex justify-between text-[12px] font-semibold text-[#6B7185]"
            }>
            <span>
              Use your mouse, or your finger on a phone.
              {needsInitials ? " Initials use the first typed style." : ""}
            </span>
            <button
              type={"button"}
              onClick={clearPad}
              className={"font-bold text-[#6B4EFF]"}>
              Clear
            </button>
          </div>
        </div>
      )}

      <label
        className={
          "flex items-start gap-2.5 rounded-[10px] bg-[#F7F8FA] p-3 text-[12.5px] font-medium leading-relaxed text-[#3A4256]"
        }>
        <input
          type={"checkbox"}
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className={"mt-0.5 accent-[#6B4EFF]"}
        />
        <span>
          I agree to sign this document electronically for {firmName}. My
          electronic signature is as valid as my handwritten signature, and a
          record of when and how I signed is kept with the document.
        </span>
      </label>
      {error && (
        <div className={"text-[13px] font-semibold text-[#B42318]"}>
          {error}
        </div>
      )}
    </PortalModal>
  );
};
